import logo from '@cannect/assets/svg/logo2.svg'
import { Illustrations } from '@cannect/new-components/atoms'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'
import { CreditCardFields } from '../NewCartCheckout/PaymentMethod/CreditCard/types'
import CardForm from '../NewCartCheckout/PaymentMethod/CreditCard/CardForm'
import PaymentMethodSelector from '../NewCartCheckout/PaymentMethod/PaymentMethodSelector'
import PaymentPercentageSelector from '../NewCartCheckout/PaymentMethod/PaymentPercentageSelector'
import { formatCardNumber } from '../NewCartCheckout/PaymentMethod/CreditCard/utils/cardUtils'
import * as S from './styles'

type PageStatus =
  | 'loading'
  | 'ready'
  | 'processing'
  | 'pix_pending'
  | 'boleto_generated'
  | 'success'
  | 'error'
  | 'invalid'

interface OrderItem {
  id: number
  quantity: number
  name: string | null
  photo: string | null
}

interface PaymentData {
  order_id: number
  amount: number
  status: string
  order: {
    id: number
    uuid: string
    patient_name: string | null
  }
  items: OrderItem[]
}

interface PixResult {
  qr_code: string
  qr_code_url: string
  expires_at: string
}

interface BoletoResult {
  line: string
  pdf: string | null
}

const initialCard = {
  card_number: '',
  card_name: '',
  card_month_validate: '',
  card_year_validate: '',
  card_cvv: '',
  installments: 1,
}

const ComplementaryPayment: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const [pageStatus, setPageStatus] = useState<PageStatus>('loading')
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)

  // Credit card state
  const [creditCard, setCreditCard] = useState(initialCard)
  const [secondCard, setSecondCard] = useState(initialCard)
  const [installments, setInstallments] = useState<any[]>([])
  const [secondInstallments, setSecondInstallments] = useState<any[]>([])
  const [isLoadingInstallments, setIsLoadingInstallments] = useState(false)
  const [isLoadingSecondInstallments, setIsLoadingSecondInstallments] = useState(false)

  // Split for combined methods
  const [splitAmounts, setSplitAmounts] = useState<{ [method: string]: number }>({})

  // Results
  const [pixResult, setPixResult] = useState<PixResult | null>(null)
  const [boletoResult, setBoletoResult] = useState<BoletoResult | null>(null)
  const [copied, setCopied] = useState(false)
  const pixPollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const formatPrice = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

  // Fetch complementary payment data
  useEffect(() => {
    api
      .get(`/complementary-payment/${token}`)
      .then((res: any) => {
        const data = res.data
        if (data.status === 'paid') {
          setPageStatus('success')
        } else if (data.status !== 'pending') {
          setPageStatus('invalid')
        } else {
          setPaymentData(data)
          setPageStatus('ready')
        }
      })
      .catch(() => setPageStatus('invalid'))
  }, [token])

  // Fetch installments for a given amount
  const fetchInstallments = useCallback(
    async (amount: number, isSecond = false) => {
      if (!paymentData?.order_id) return
      const setLoading = isSecond ? setIsLoadingSecondInstallments : setIsLoadingInstallments
      const setList = isSecond ? setSecondInstallments : setInstallments
      setLoading(true)
      try {
        const res: any = await api.get(
          `/payments/installments_public/${paymentData.order_id}?value_per_card=${amount}`
        )
        if (res.data?.installments) {
          setList(res.data.installments)
        }
      } catch {
        setList([])
      } finally {
        setLoading(false)
      }
    },
    [paymentData?.order_id]
  )

  // When credit card method is selected, fetch installments
  useEffect(() => {
    if (!paymentData?.amount) return
    if (
      selectedMethod === 'credit_card' ||
      selectedMethod === 'credit_card_pix' ||
      selectedMethod === 'credit_card_boleto' ||
      selectedMethod === 'credit_card_credit_card'
    ) {
      const firstAmount = splitAmounts['credit_card'] || paymentData.amount
      fetchInstallments(firstAmount, false)
    }
    if (selectedMethod === 'credit_card_credit_card') {
      const secondAmount = splitAmounts['credit_card_2'] || paymentData.amount / 2
      fetchInstallments(secondAmount, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod, paymentData?.amount])

  useEffect(() => {
    return () => {
      if (pixPollingRef.current) clearInterval(pixPollingRef.current)
    }
  }, [])

  const changeCreditCardInfo = useCallback((field: CreditCardFields, value: string | number) => {
    setCreditCard((prev) => ({
      ...prev,
      [field]: field === 'card_number' ? formatCardNumber(String(value)) : value,
    }))
  }, [])

  const changeSecondCardInfo = useCallback((field: CreditCardFields, value: string | number) => {
    setSecondCard((prev) => ({
      ...prev,
      [field]: field === 'card_number' ? formatCardNumber(String(value)) : value,
    }))
  }, [])

  const buildCardPayload = (card: typeof initialCard) => ({
    number: card.card_number.replace(/\s/g, ''),
    holder_name: card.card_name.trim().toUpperCase(),
    exp_month: card.card_month_validate,
    exp_year: card.card_year_validate,
    cvv: String(card.card_cvv),
  })

  const startPixPolling = () => {
    pixPollingRef.current = setInterval(async () => {
      try {
        const statusRes: any = await api.get(`/complementary-payment/${token}`)
        if (statusRes.data.status === 'paid') {
          if (pixPollingRef.current) clearInterval(pixPollingRef.current)
          setPageStatus('success')
        }
      } catch {}
    }, 5000)
  }

  const handleSubmit = async () => {
    if (!selectedMethod || !paymentData) return

    const totalAmount = paymentData.amount

    // Validate card fields
    if (
      ['credit_card', 'credit_card_pix', 'credit_card_boleto', 'credit_card_credit_card'].includes(selectedMethod)
    ) {
      if (
        !creditCard.card_number ||
        !creditCard.card_name ||
        !creditCard.card_month_validate ||
        !creditCard.card_year_validate ||
        !creditCard.card_cvv
      ) {
        toast.error('Preencha todos os dados do cartão')
        return
      }
    }
    if (selectedMethod === 'credit_card_credit_card') {
      if (
        !secondCard.card_number ||
        !secondCard.card_name ||
        !secondCard.card_month_validate ||
        !secondCard.card_year_validate ||
        !secondCard.card_cvv
      ) {
        toast.error('Preencha todos os dados do segundo cartão')
        return
      }
    }

    setPageStatus('processing')

    try {
      let payload: any

      if (selectedMethod === 'pix') {
        payload = { payment_method: 'pix' }
      } else if (selectedMethod === 'credit_card') {
        payload = {
          payment_method: 'credit_card',
          installments: creditCard.installments || 1,
          card: buildCardPayload(creditCard),
        }
      } else if (selectedMethod === 'boleto') {
        payload = { payment_method: 'boleto' }
      } else {
        // Combined methods — use payments array
        const firstAmount = splitAmounts['credit_card'] ?? totalAmount / 2
        const secondAmount = totalAmount - firstAmount

        const payments: any[] = [
          {
            payment_method: 'credit_card',
            installments: creditCard.installments || 1,
            card: buildCardPayload(creditCard),
            amount: firstAmount,
          },
        ]

        if (selectedMethod === 'credit_card_pix') {
          payments.push({ payment_method: 'pix', amount: secondAmount })
        } else if (selectedMethod === 'credit_card_boleto') {
          payments.push({ payment_method: 'boleto', amount: secondAmount })
        } else if (selectedMethod === 'credit_card_credit_card') {
          payments.push({
            payment_method: 'credit_card',
            installments: secondCard.installments || 1,
            card: buildCardPayload(secondCard),
            amount: secondAmount,
          })
        }

        payload = { payments }
      }

      const res: any = await api.post(`/complementary-payment/${token}/charge`, payload)

      // Find PIX result (if any)
      const charges = res.data.charges || [res.data]
      const pixCharge = charges.find((c: any) => c.payment_method === 'pix')
      const boletoCharge = charges.find((c: any) => c.payment_method === 'boleto')

      if (pixCharge?.qr_code) {
        setPixResult({
          qr_code: pixCharge.qr_code,
          qr_code_url: pixCharge.qr_code_url,
          expires_at: pixCharge.expires_at,
        })
        setPageStatus('pix_pending')
        startPixPolling()
      } else if (boletoCharge?.boleto_line) {
        setBoletoResult({ line: boletoCharge.boleto_line, pdf: boletoCharge.boleto_pdf || null })
        setPageStatus('boleto_generated')
      } else {
        setPageStatus('success')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao processar pagamento')
      setPageStatus('ready')
    }
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 3000)
  }

  const handleMethodSelect = (method: string) => {
    setSelectedMethod(method)
    setSplitAmounts({})
    setCreditCard(initialCard)
    setSecondCard(initialCard)
  }

  const handleSplitUpdate = ({
    calculatedPaymentValues,
  }: {
    percentage: number
    calculatedPaymentValues: { [method: string]: number }
  }) => {
    const methods = Object.keys(calculatedPaymentValues)
    setSplitAmounts({
      credit_card: calculatedPaymentValues[methods[0]],
      credit_card_2: calculatedPaymentValues[methods[1]],
    })
  }

  const isCombinedMethod = (method: string | null) =>
    method?.startsWith('credit_card_') && method !== 'credit_card'

  const getSubmitLabel = () => {
    if (pageStatus === 'processing') return 'Processando...'
    if (selectedMethod === 'pix') return 'Gerar PIX'
    if (selectedMethod === 'boleto') return 'Gerar Boleto'
    if (selectedMethod === 'credit_card_pix') return `Pagar cartão + gerar PIX`
    if (selectedMethod === 'credit_card_boleto') return `Pagar cartão + gerar Boleto`
    return `Pagar ${formatPrice(paymentData?.amount || 0)}`
  }

  // ─── Loading / invalid / success screens ──────────────────────────────────
  if (pageStatus === 'loading') {
    return (
      <S.PageWrapper>
        <S.CenteredCard>
          <S.Logo src={logo} alt="Cannect" />
          <S.LoadingText>Carregando...</S.LoadingText>
        </S.CenteredCard>
      </S.PageWrapper>
    )
  }

  if (pageStatus === 'invalid') {
    return (
      <S.PageWrapper>
        <S.CenteredCard>
          <S.Logo src={logo} alt="Cannect" />
          <Illustrations.RefusedPayment />
          <S.Title>Link inválido ou expirado</S.Title>
          <S.Subtitle>Este link de pagamento não é mais válido. Entre em contato com a Cannect.</S.Subtitle>
        </S.CenteredCard>
      </S.PageWrapper>
    )
  }

  if (pageStatus === 'success') {
    return (
      <S.PageWrapper>
        <S.CenteredCard>
          <S.Logo src={logo} alt="Cannect" />
          <Illustrations.SuccessPayment />
          <S.Title>Pagamento confirmado!</S.Title>
          <S.Subtitle>
            Seu pagamento complementar foi recebido. Em breve você receberá uma confirmação por email.
          </S.Subtitle>
        </S.CenteredCard>
      </S.PageWrapper>
    )
  }

  // ─── Main payment page ─────────────────────────────────────────────────────
  return (
    <S.PageWrapper>
      <S.ContentWrapper>
        <S.LogoCentered src={logo} alt="Cannect" />

        {/* Order summary */}
        <S.SummaryCard>
          <S.OrderTitle>Atualização do seu pedido</S.OrderTitle>
          <S.OrderDescription>
            Realizamos uma atualização no seu pedido conforme alinhado previamente. Abaixo você
            encontra os detalhes da troca/inclusão de produtos e o valor do pagamento complementar.
            Após a confirmação do pagamento, daremos continuidade ao processo normalmente.
          </S.OrderDescription>
          {paymentData?.order?.patient_name && (
            <S.OrderDetail>
              Paciente: <strong>{paymentData.order.patient_name}</strong>
            </S.OrderDetail>
          )}
          <S.OrderDetail>
            Pedido: <strong>#{paymentData?.order?.id}</strong>
          </S.OrderDetail>
          <S.AmountBox>
            <span>Valor a pagar</span>
            <strong>{formatPrice(paymentData?.amount || 0)}</strong>
          </S.AmountBox>

          {paymentData?.items && paymentData.items.length > 0 && (
            <S.ItemsList style={{ marginTop: 16 }}>
              <S.SectionTitle>Produtos</S.SectionTitle>
              {paymentData.items.map((item) => (
                <S.ItemRow key={item.id}>
                  {item.photo ? (
                    <S.ItemPhoto src={item.photo} alt={item.name || ''} />
                  ) : (
                    <S.ItemPhotoPlaceholder />
                  )}
                  <S.ItemInfo>
                    <S.ItemName>{item.name || 'Produto'}</S.ItemName>
                    <S.ItemQty>Qtd: {item.quantity}</S.ItemQty>
                  </S.ItemInfo>
                </S.ItemRow>
              ))}
            </S.ItemsList>
          )}
        </S.SummaryCard>

        {/* PIX pending — show QR code */}
        {pageStatus === 'pix_pending' && pixResult && (
          <S.PaymentCard>
            <S.SectionTitle>Escaneie o QR Code</S.SectionTitle>
            {pixResult.qr_code_url && (
              <S.QrCodeImage src={pixResult.qr_code_url} alt="QR Code PIX" />
            )}
            <S.CopySection>
              <S.PixCode>{pixResult.qr_code}</S.PixCode>
              <S.CopyButton onClick={() => handleCopy(pixResult.qr_code)}>
                {copied ? 'Copiado!' : 'Copiar código'}
              </S.CopyButton>
            </S.CopySection>
            <S.PixNote>Aguardando confirmação do pagamento...</S.PixNote>
          </S.PaymentCard>
        )}

        {/* Boleto generated */}
        {pageStatus === 'boleto_generated' && boletoResult && (
          <S.PaymentCard>
            <S.SectionTitle>Boleto gerado</S.SectionTitle>
            <S.CopySection>
              <S.PixCode>{boletoResult.line}</S.PixCode>
              <S.CopyButton onClick={() => handleCopy(boletoResult.line)}>
                {copied ? 'Copiado!' : 'Copiar código'}
              </S.CopyButton>
            </S.CopySection>
            {boletoResult.pdf && (
              <S.PayButton
                as="a"
                href={boletoResult.pdf}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textAlign: 'center', textDecoration: 'none', display: 'block' }}>
                Abrir boleto em PDF
              </S.PayButton>
            )}
            <S.PixNote>Prazo de pagamento: 3 dias úteis.</S.PixNote>
          </S.PaymentCard>
        )}

        {/* Payment selector + forms */}
        {!['pix_pending', 'boleto_generated'].includes(pageStatus) && (
          <S.PaymentCard>
            {!selectedMethod ? (
              <PaymentMethodSelector
                selectedMethod={selectedMethod}
                onSelectMethod={handleMethodSelect}
                totalAmount={paymentData?.amount || 0}
                isPagaleveEligible={false}
                isCheckingPagaleve={false}
                hasMultipleMethods
              />
            ) : (
              <div>
                <S.BackButton onClick={() => setSelectedMethod(null)}>
                  <ArrowLeft size={18} />
                  Formas de pagamento
                </S.BackButton>

                {/* Combined method: percentage split selector */}
                {isCombinedMethod(selectedMethod) && paymentData && (
                  <div style={{ marginTop: 16 }}>
                    <PaymentPercentageSelector
                      totalAmount={paymentData.amount}
                      paymentMethods={['Cartão 1', selectedMethod === 'credit_card_credit_card' ? 'Cartão 2' : selectedMethod === 'credit_card_pix' ? 'PIX' : 'Boleto']}
                      onPaymentUpdate={handleSplitUpdate}
                    />
                  </div>
                )}

                {/* First card form (for all credit_card methods) */}
                {(selectedMethod === 'credit_card' ||
                  selectedMethod === 'credit_card_pix' ||
                  selectedMethod === 'credit_card_boleto' ||
                  selectedMethod === 'credit_card_credit_card') && (
                  <div style={{ marginTop: 16 }}>
                    {isCombinedMethod(selectedMethod) && (
                      <S.CardLabel>
                        Cartão 1 — {formatPrice(splitAmounts['credit_card'] ?? (paymentData?.amount || 0) / 2)}
                      </S.CardLabel>
                    )}
                    <CardForm
                      creditCard={creditCard}
                      changeCreditCardInfo={changeCreditCardInfo}
                      installments={installments}
                      isLoadingInstallments={isLoadingInstallments}
                    />
                  </div>
                )}

                {/* Second card form (for credit_card_credit_card) */}
                {selectedMethod === 'credit_card_credit_card' && (
                  <div style={{ marginTop: 16 }}>
                    <S.CardLabel>
                      Cartão 2 — {formatPrice(splitAmounts['credit_card_2'] ?? (paymentData?.amount || 0) / 2)}
                    </S.CardLabel>
                    <CardForm
                      creditCard={secondCard}
                      changeCreditCardInfo={changeSecondCardInfo}
                      installments={secondInstallments}
                      isLoadingInstallments={isLoadingSecondInstallments}
                    />
                  </div>
                )}

                {/* PIX info */}
                {(selectedMethod === 'pix' || selectedMethod === 'credit_card_pix') && (
                  <S.PixInfo style={{ marginTop: 16 }}>
                    <S.PixIcon>💠</S.PixIcon>
                    {selectedMethod === 'credit_card_pix' ? (
                      <p>
                        Após pagar o cartão, um QR Code PIX será gerado para o valor de{' '}
                        <strong>{formatPrice(splitAmounts['credit_card_2'] ?? (paymentData?.amount || 0) / 2)}</strong>.
                      </p>
                    ) : (
                      <>
                        <p>Clique em "Gerar PIX" para receber o QR Code.</p>
                        <p>O código tem validade de 1 hora.</p>
                      </>
                    )}
                  </S.PixInfo>
                )}

                {/* Boleto info */}
                {(selectedMethod === 'boleto' || selectedMethod === 'credit_card_boleto') && (
                  <S.PixInfo style={{ marginTop: 16 }}>
                    <S.PixIcon>📄</S.PixIcon>
                    {selectedMethod === 'credit_card_boleto' ? (
                      <p>
                        Após pagar o cartão, um boleto será gerado para o valor de{' '}
                        <strong>{formatPrice(splitAmounts['credit_card_2'] ?? (paymentData?.amount || 0) / 2)}</strong>.
                      </p>
                    ) : (
                      <>
                        <p>Clique em "Gerar Boleto" para receber o código de barras.</p>
                        <p>Prazo de vencimento: 3 dias úteis.</p>
                      </>
                    )}
                  </S.PixInfo>
                )}

                <S.PayButton
                  onClick={handleSubmit}
                  disabled={pageStatus === 'processing'}
                  style={{ marginTop: 24 }}>
                  {getSubmitLabel()}
                </S.PayButton>
              </div>
            )}
          </S.PaymentCard>
        )}
      </S.ContentWrapper>
    </S.PageWrapper>
  )
}

export default ComplementaryPayment
